// GexTrendMonitor — tabbed intraday trend visualization.
//
// GTS tab — Polarity Ribbon:
// - gexTrendStrength.spx
// - keeps the stored GTS value as an absolute magnitude
// - CALL observations render in the upper lane
// - PUT observations render in the lower lane
// - same-side observations connect; polarity flips do NOT draw a fake path
//   through zero
// - stems show instantaneous pressure, diamonds mark polarity flips
// - mirrored 0.20 / 0.30 / 0.40 strength zones
//
// Call / Put GEX tab:
// - greekExposures.spx filtered to greek_type === 'gamma'
// - plots calls_total and puts_total directly
//
// Mouse wheel zooms around the crosshair.
// Click-drag pans.
// Double-click or Reset restores the full session.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { WidgetPanelControls, WidgetPanelLiveStatus } from '../Dashboard/WidgetPanelSlot';
import useGreeksWebsocketDataStore from '../../Store/GreeksWebsocketDataStore';
import usePlaybackStore from '../../Store/playbackStore';
import useWidgetMinuteFocusStore, {
  useFocusedMinuteSourceCleanup,
} from '../../Store/widgetMinuteFocusStore';
import { isDuringMarketHours, latestTradeDayOnly } from '../../Utils/marketHours';
import { focusedPointIndex } from '../../Utils/widgetMinuteFocus';
import { PLOT_PAN_THRESHOLD_PX } from '../../Utils/chartTimeWindow';
import {
  denseSeriesGapRanges,
  forwardFillSeriesAtGaps,
  mergeSeriesGapRanges,
  replaySeriesGapRanges,
} from '../../Utils/seriesPresentation';
import { gexTimeAxisTicks, zoomGexTimeRange } from '../../Utils/gexTimeWindow';
import useGexSharedTimeWindow from './useGexSharedTimeWindow';
import useNonPassiveWheel from './useNonPassiveWheel';
import '../../Styles/GexTrendMonitor.css';

const ML = 8;
const MR = 50;
const MT = 8;
const MB = 16;

const GTS_TRANSITION_BAND = 0.20;

const GTS_LEVELS = [
  { value: 0.20, label: '0.20' },
  { value: 0.30, label: '0.30' },
  { value: 0.40, label: '0.40' },
  { value: 0.50, label: '0.50' },
  { value: 0.60, label: '0.60' },
];

const COLORS = {
  call: 'rgb(0,191,58)',
  put: 'rgb(208,0,0)',
  neutral: 'rgba(140,148,160,0.8)',
};

const MIN_TIME_LABEL_GAP_PX = 46;

function selectSpacedTimeTicks(
  candidates,
  xFn,
  minGap = MIN_TIME_LABEL_GAP_PX,
) {
  const sorted = [...new Set(candidates)].sort((a, b) => a - b);

  if (sorted.length <= 2) return sorted;

  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const selected = [first];

  for (let i = 1; i < sorted.length - 1; i += 1) {
    const index = sorted[i];

    const previousX = xFn(selected[selected.length - 1]);
    const currentX = xFn(index);
    const lastX = xFn(last);

    if (
      currentX - previousX >= minGap
      && lastX - currentX >= minGap
    ) {
      selected.push(index);
    }
  }

  if (last !== first) {
    selected.push(last);
  }

  return selected;
}

const clamp = (value, min, max) =>
  Math.max(min, Math.min(max, value));

const scaleLinear = ([d0, d1], [r0, r1]) => (value) =>
  d1 === d0
    ? r0
    : r0 + ((value - d0) / (d1 - d0)) * (r1 - r0);

function toEpochMs(value) {
  if (value == null) return null;

  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value > 1e12) return value;
    if (value > 1e9) return value * 1000;
    return null;
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    if (numeric > 1e12) return numeric;
    if (numeric > 1e9) return numeric * 1000;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function fmtAxisTime(epochMs) {
  try {
    return new Date(epochMs).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/New_York',
    });
  } catch {
    return '';
  }
}

function fmtCompact(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';

  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';

  if (abs >= 1_000_000_000) {
    return `${sign}${(abs / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 1 : 2)}B`;
  }

  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}M`;
  }

  if (abs >= 1_000) {
    return `${sign}${(abs / 1_000).toFixed(abs >= 10_000 ? 1 : 2)}k`;
  }

  return `${Math.round(n)}`;
}

function normalizeTrendType(value) {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === 'call' || normalized === 'bullish') {
    return 'call';
  }

  if (normalized === 'put' || normalized === 'bearish') {
    return 'put';
  }

  return 'neutral';
}

function trendColor(value) {
  return COLORS[normalizeTrendType(value)] || COLORS.neutral;
}

function gtsDisplayValue(point) {
  if (!point) return 0;

  const side = normalizeTrendType(point.trendType);

  if (side === 'call') return Math.abs(point.value);
  if (side === 'put') return -Math.abs(point.value);

  return 0;
}

function buildGtsSeries(rows) {
  const mapped = (rows || [])
    .map((row) => {
      const t = toEpochMs(row?.timestamp ?? row?.formatted_date);
      const value = Number(row?.value);

      if (!Number.isFinite(t) || !Number.isFinite(value)) {
        return null;
      }

      return {
        t,
        value: Math.abs(value),
        trendType: row?.trend_type ?? null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.t - b.t);

  return latestTradeDayOnly(
    mapped.filter((point) => isDuringMarketHours(point.t)),
  );
}

function buildGammaSeries(rows) {
  const mapped = (rows || [])
    .filter(
      (row) =>
        String(row?.greek_type || '').trim().toLowerCase() === 'gamma',
    )
    .map((row) => {
      const t = toEpochMs(row?.timestamp ?? row?.formatted_date);
      const call = Number(row?.calls_total);
      const put = Number(row?.puts_total);

      if (
        !Number.isFinite(t)
        || !Number.isFinite(call)
        || !Number.isFinite(put)
      ) {
        return null;
      }

      return {
        t,
        call,
        put,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.t - b.t);

  return latestTradeDayOnly(
    mapped.filter((point) => isDuringMarketHours(point.t)),
  );
}

function nearestSeriesIndex(series, targetTime) {
  if (!series.length || !Number.isFinite(targetTime)) return null;

  let low = 0;
  let high = series.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (series[middle].t < targetTime) low = middle + 1;
    else high = middle;
  }

  return [low, low - 1]
    .filter((index) => index >= 0 && index < series.length)
    .reduce((best, index) => (
      best == null
      || Math.abs(series[index].t - targetTime) < Math.abs(series[best].t - targetTime)
        ? index
        : best
    ), null);
}

export function seriesIndexAtPlotX(series, x, plotLeft, plotRight, domainStart, domainEnd) {
  const fraction = clamp((x - plotLeft) / Math.max(1, plotRight - plotLeft), 0, 1);
  const targetTime = domainStart + fraction * (domainEnd - domainStart);
  return nearestSeriesIndex(series, targetTime);
}

export default function GexTrendMonitor() {
  const gtsRows = useGreeksWebsocketDataStore(
    (state) => state.gexTrendStrength?.spx,
  );
  const exposureRows = useGreeksWebsocketDataStore(
    (state) => state.greekExposures?.spx,
  );
  const liveGreekGaps = useGreeksWebsocketDataStore((state) => state.liveGapRanges || []);
  const replaying = usePlaybackStore((state) => state.mode === 'playing');
  const replayCoverage = usePlaybackStore((state) => state.replayCoverage);
  const replayCutoffMs = usePlaybackStore((state) => state.replayCutoffMs);

  const [activeTab, setActiveTab] = useState('gts');
  const [size, setSize] = useState({ w: 320, h: 120 });
  const [hover, setHover] = useState(null);
  const [viewRange, setViewRange] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const focusedMinuteMs = useWidgetMinuteFocusStore((state) => state.minuteMs);
  const focusSourceId = useWidgetMinuteFocusStore((state) => state.sourceId);
  const setFocusedMinute = useWidgetMinuteFocusStore((state) => state.setFocusedMinute);
  const clearFocusedMinute = useWidgetMinuteFocusStore((state) => state.clearFocusedMinute);

  useFocusedMinuteSourceCleanup('gexTrendMonitor');

  const roRef = useRef(null);
  const panRef = useRef(null);
  const viewDetachedRef = useRef(false);

  const wrapRef = useCallback((node) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }

    if (!node) return;

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;

      setSize({
        w: Math.max(1, Math.round(width)),
        h: Math.max(1, Math.round(height)),
      });
    });

    ro.observe(node);
    roRef.current = ro;
  }, []);

  const gtsSeries = useMemo(
    () => buildGtsSeries(gtsRows),
    [gtsRows],
  );

  const gammaSeries = useMemo(
    () => buildGammaSeries(exposureRows),
    [exposureRows],
  );

  const observedSeries =
    activeTab === 'gts'
      ? gtsSeries
      : gammaSeries;

  const activeDataset = activeTab === 'gts' ? 'greeks:gexTrend' : 'greeks:exposures';
  const gaps = useMemo(() => mergeSeriesGapRanges(
    replaying
      ? replaySeriesGapRanges(replayCoverage, [activeDataset], replayCutoffMs)
      : liveGreekGaps,
    denseSeriesGapRanges(observedSeries, { source: activeDataset }),
  ), [activeDataset, liveGreekGaps, observedSeries, replaying, replayCoverage, replayCutoffMs]);
  const series = useMemo(
    () => forwardFillSeriesAtGaps(observedSeries, gaps),
    [gaps, observedSeries],
  );
  const {
    domainMs,
    minSpanMs,
    publishSession,
    schedulePublish,
  } = useGexSharedTimeWindow({
    series,
    sourceId: 'gex-trend-monitor',
    setViewRange,
    viewDetachedRef,
  });

  const wheelHandlerRef = useRef(null);
  const chartWheelRef = useNonPassiveWheel(wheelHandlerRef);
  const zoomBaseRef = useRef(null);

  const n = series.length;
  const { w: VW, h: VH } = size;

  const fullStart = series[0]?.t ?? 0;
  const fullEnd = series[n - 1]?.t ?? fullStart;

  let viewStart = viewRange?.start ?? fullStart;
  let viewEnd = viewRange?.end ?? fullEnd;

  const domainStart = domainMs?.from ?? fullStart;
  const domainEnd = domainMs?.to ?? fullEnd;
  viewStart = clamp(viewStart, domainStart, domainEnd);
  viewEnd = clamp(viewEnd, viewStart, domainEnd);

  if (fullEnd > fullStart && viewEnd - viewStart < 60_000) {
    viewEnd = Math.min(fullEnd, viewStart + 60_000);
    viewStart = Math.max(fullStart, viewEnd - 60_000);
  }

  // Every gesture composes on this rather than on the rendered range. A
  // trackpad delivers a burst of wheel events inside a single task, and all of
  // them would otherwise start from the same pre-burst range, so eight events
  // of input produced one event's worth of zoom.
  zoomBaseRef.current = { from: viewStart, to: viewEnd };

  const isZoomed =
    viewRange != null
    && (
      viewStart > fullStart + 0.01
      || viewEnd < fullEnd - 0.01
    );

  // The window is clamped to the session close, not to the last point, so it
  // can sit entirely outside the data. Clamping the missing index to 0 made the
  // whole series "visible": the polylines drew off-plot and the y-axis
  // autoscaled to the full session behind an empty plot.
  const firstInWindow = series.findIndex((point) => point.t >= viewStart);
  let lastInWindow = firstInWindow < 0 ? -1 : n - 1;
  while (lastInWindow >= 0 && series[lastInWindow].t > viewEnd) {
    lastInWindow -= 1;
  }
  const hasVisiblePoints = firstInWindow >= 0 && lastInWindow >= firstInWindow;
  const visibleStartIdx = hasVisiblePoints ? firstInWindow : 0;
  const visibleEndIdx = hasVisiblePoints ? lastInWindow : -1;

  const visibleSeries = hasVisiblePoints
    ? series.slice(visibleStartIdx, visibleEndIdx + 1)
    : [];
  const xTimeFn = scaleLinear(
    [viewStart, Math.max(viewStart + 60_000, viewEnd)],
    [ML, VW - MR],
  );

  let yFn = scaleLinear([0, 1], [VH - MB, MT]);
  let yTicks = [];
  let gtsHi = 0.60;

  if (activeTab === 'gts') {
    const visibleValues = visibleSeries
      .map((point) => Math.abs(Number(point.value)))
      .filter(Number.isFinite);

    const maxValue = Math.max(0.60, ...visibleValues);
    gtsHi = Math.max(0.60, maxValue * 1.08);

    // The negative half is a visual PUT lane only. The source GTS values remain
    // absolute magnitudes; no signed GTS value is created or stored.
    yFn = scaleLinear(
      [-gtsHi, gtsHi],
      [VH - MB, MT],
    );

    yTicks = [
      { value: 0, label: '0' },
      ...GTS_LEVELS,
      ...GTS_LEVELS.map((level) => ({
        value: -level.value,
        label: level.label,
      })),
    ];
  } else {
    const visibleValues = visibleSeries
      .flatMap((point) => [Number(point.call), Number(point.put)])
      .filter(Number.isFinite);

    let lo = Math.min(0, ...visibleValues);
    let hi = Math.max(0, ...visibleValues);

    if (hi === lo) {
      const spread = Math.abs(hi) || 1;
      lo -= spread;
      hi += spread;
    }

    const pad = (hi - lo) * 0.10 || 1;

    yFn = scaleLinear(
      [lo - pad, hi + pad],
      [VH - MB, MT],
    );

    const mid = (lo + hi) / 2;

    yTicks = [
      { value: hi, label: fmtCompact(hi) },
      { value: mid, label: fmtCompact(mid) },
      { value: 0, label: '0' },
      { value: lo, label: fmtCompact(lo) },
    ].filter(
      (tick, index, arr) =>
        arr.findIndex(
          (other) =>
            Math.abs(other.value - tick.value)
            < Math.max(1, Math.abs(tick.value) * 0.001),
        ) === index,
    );
  }

  const gtsGeometry =
    activeTab === 'gts'
      ? (() => {
          const segments = [];
          const stems = [];
          const bridges = [];
          const flips = [];

          let runLength = 0;
          let previousSide = null;

          visibleSeries.forEach((point, offset) => {
            const side = normalizeTrendType(point.trendType);
            const magnitude = Math.abs(Number(point.value)) || 0;
            const displayValue = gtsDisplayValue(point);
            const lowConviction = magnitude <= GTS_TRANSITION_BAND;

            if (side === previousSide && side !== 'neutral') {
              runLength += 1;
            } else {
              runLength = side === 'neutral' ? 0 : 1;
            }

            const significance = clamp(
              (magnitude - GTS_TRANSITION_BAND)
                / Math.max(0.01, 0.60 - GTS_TRANSITION_BAND),
              0,
              1,
            );

            stems.push({
              key: `stem:${point.t}`,
              x: xTimeFn(point.t),
              y: yFn(displayValue),
              yZero: yFn(0),
              color: lowConviction
                ? COLORS.neutral
                : trendColor(point.trendType),
              opacity: lowConviction
                ? 0.035
                : Math.min(
                    0.30,
                    0.10
                    + significance * 0.18
                    + Math.min(runLength, 20) * 0.001,
                  ),
            });

            if (offset > 0) {
              const prev = visibleSeries[offset - 1];
              const prevSide = normalizeTrendType(prev.trendType);
              const prevMagnitude = Math.abs(Number(prev.value)) || 0;
              const prevDisplayValue = gtsDisplayValue(prev);
              const prevLowConviction =
                prevMagnitude <= GTS_TRANSITION_BAND;
              const x1 = xTimeFn(prev.t);
              const x2 = xTimeFn(point.t);

              if (side !== 'neutral'
                && prevSide !== 'neutral'
                && side === prevSide
              ) {
                const persistence = Math.min(runLength, 20);
                const inTransition =
                  lowConviction || prevLowConviction;

                segments.push({
                  key: `segment:${prev.t}:${point.t}`,
                  x1,
                  y1: yFn(prevDisplayValue),
                  x2,
                  y2: yFn(displayValue),
                  color: inTransition
                    ? COLORS.neutral
                    : trendColor(point.trendType),
                  width: inTransition
                    ? 1.35
                    : 1.35 + persistence * 0.045,
                  opacity: inTransition
                    ? 0.62
                    : Math.min(1, 0.76 + persistence * 0.012),
                });
              } else if (side !== 'neutral'
                && prevSide !== 'neutral'
                && side !== prevSide
              ) {
                const midX = (x1 + x2) / 2;
                const bothLow =
                  lowConviction && prevLowConviction;

                // The bridge is explicitly a visual state handoff. It is not a
                // calculated GTS path through zero.
                bridges.push(
                  {
                    key: `bridge-a:${prev.t}:${point.t}`,
                    x1,
                    y1: yFn(prevDisplayValue),
                    x2: midX,
                    y2: yFn(0),
                    opacity: bothLow ? 0.34 : 0.58,
                  },
                  {
                    key: `bridge-b:${prev.t}:${point.t}`,
                    x1: midX,
                    y1: yFn(0),
                    x2,
                    y2: yFn(displayValue),
                    opacity: bothLow ? 0.34 : 0.58,
                  },
                );

                // Repeated low-strength flip chatter stays muted inside the
                // contested band. A stronger handoff gets an explicit marker.
                if (!bothLow) {
                  flips.push({
                    key: `flip:${prev.t}:${point.t}`,
                    x: midX,
                    y: yFn(0),
                    color: trendColor(point.trendType),
                  });
                }
              }
            }

            previousSide = side;
          });

          return {
            segments,
            stems,
            bridges,
            flips,
          };
        })()
      : {
          segments: [],
          stems: [],
          bridges: [],
          flips: [],
        };


  const synchronizedIndex = focusSourceId !== 'gexTrendMonitor'
    ? focusedPointIndex(series, focusedMinuteMs, visibleStartIdx, visibleEndIdx)
    : null;
  const hoverPoint =
    hover != null
    && hover.idx >= 0
    && hover.idx < n
      ? series[hover.idx]
      : null;
  const synchronizedPoint = synchronizedIndex == null ? null : series[synchronizedIndex];
  const crosshairPoint = hoverPoint ?? synchronizedPoint;
  const latestPoint = n > 0 ? series[n - 1] : null;
  const displayPoint = hoverPoint ?? latestPoint;
  const hoverX = crosshairPoint ? xTimeFn(crosshairPoint.t) : null;

  let hoverY = null;

  if (hoverPoint) {
    hoverY =
      activeTab === 'gts'
        ? yFn(gtsDisplayValue(hoverPoint))
        : yFn(
            Math.abs(hoverPoint.call) >= Math.abs(hoverPoint.put)
              ? hoverPoint.call
              : hoverPoint.put,
          );
  }

  const resetZoom = () => {
    publishSession();
    setHover(null);
    clearFocusedMinute('gexTrendMonitor');
    panRef.current = null;
    setIsPanning(false);
  };

  const changeTab = (nextTab) => {
    if (nextTab === activeTab) return;

    setActiveTab(nextTab);
    // GTS and Gamma are independent datasets that can lag each other, so a
    // detached local pan from one tab must not carry onto the other. Dropping
    // the range here shows the incoming dataset whole for the frame before the
    // shared-window effect resolves it against that tab's own extent; resolving
    // it in this handler would commit a range measured on the outgoing one.
    viewDetachedRef.current = false;
    setViewRange(null);
    setHover(null);
    clearFocusedMinute('gexTrendMonitor');
    panRef.current = null;
    setIsPanning(false);
  };

  const handlePointerMove = (event) => {
    if (n <= 0) {
      clearFocusedMinute('gexTrendMonitor');
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const mx = event.clientX - rect.left;
    const plotWidth = Math.max(1, VW - MR - ML);

    const pan = panRef.current;

    if (pan) {
      const dx = event.clientX - pan.startX;
      if (!pan.active && Math.abs(dx) < PLOT_PAN_THRESHOLD_PX) return;
      if (!pan.active) {
        pan.active = true;
        setIsPanning(true);
      }
      const span = pan.startRange.end - pan.startRange.start;
      const shift = -(dx / plotWidth) * span;
      // Local until the store accepts the publish below. Replay stores the
      // pinned position for its trading date and preserves it across scrubs.
      viewDetachedRef.current = true;

      let nextStart = pan.startRange.start + shift;
      let nextEnd = pan.startRange.end + shift;

      if (nextStart < domainStart) {
        nextEnd += domainStart - nextStart;
        nextStart = domainStart;
      }

      if (nextEnd > domainEnd) {
        nextStart -= nextEnd - domainEnd;
        nextEnd = domainEnd;
      }

      const panned = {
        from: Math.max(domainStart, nextStart),
        to: Math.min(domainEnd, nextEnd),
      };
      zoomBaseRef.current = panned;
      setViewRange({ start: panned.from, end: panned.to });
      // `positionIsIntent` keeps an interior pan absolute. Without it the
      // classifier reads the range as a duration and re-anchors it to each
      // chart's own data edge, which moves them somewhere nobody dragged.
      schedulePublish(panned, { positionIsIntent: true });
    }

    // A window holding no point has nothing under the cursor. The index lookup
    // takes no distance tolerance, so it would snap to the nearest bar outside
    // the window and publish that minute to every synchronized widget.
    if (!hasVisiblePoints || mx < ML || mx > VW - MR) {
      setHover(null);
      clearFocusedMinute('gexTrendMonitor');
      return;
    }

    const idx = seriesIndexAtPlotX(series, mx, ML, VW - MR, viewStart, viewEnd);

    setHover({ idx });
    setFocusedMinute('gexTrendMonitor', series[idx].t);
  };

  const handlePointerDown = (event) => {
    if (event.button !== 0 || n <= 1) return;

    event.currentTarget.setPointerCapture?.(event.pointerId);

    panRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startRange: {
        start: viewStart,
        end: viewEnd,
      },
      active: false,
    };
  };

  const finishPan = (event) => {
    if (
      !panRef.current
      || (
        event?.pointerId != null
        && event.pointerId !== panRef.current.pointerId
      )
    ) {
      return;
    }

    if (event?.currentTarget && event.pointerId != null) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }

    panRef.current = null;
    setIsPanning(false);
  };

  const handleWheel = (event) => {
    // Claim the gesture before data and delta checks. The plot exists with one
    // or two rows, and the dashboard must not scroll while the pointer is on it.
    event.preventDefault();
    if (n <= 2 || !domainMs) return;
    if (event.deltaY === 0) return;

    const plotWidth = Math.max(1, VW - MR - ML);
    const range = zoomBaseRef.current;

    const rect = event.currentTarget.getBoundingClientRect();

    const mx = clamp(
      event.clientX - rect.left,
      ML,
      VW - MR,
    );

    const anchorFrac = clamp(
      (mx - ML) / plotWidth,
      0,
      1,
    );

    const nextRange = zoomGexTimeRange({
      range,
      domain: domainMs,
      deltaY: event.deltaY,
      deltaMode: event.deltaMode,
      plotWidth,
      anchorFraction: anchorFrac,
      minSpan: minSpanMs,
    });
    if (!nextRange) return;

    viewDetachedRef.current = false;
    zoomBaseRef.current = nextRange;
    setViewRange({ start: nextRange.from, end: nextRange.to });
    schedulePublish(nextRange);
  };


  wheelHandlerRef.current = handleWheel;

  return (
    <div className="gtm-panel">
      <WidgetPanelLiveStatus />
      <WidgetPanelControls>
        <button
          type="button"
          className={activeTab === 'gts' ? 'gtm-tab is-active' : 'gtm-tab'}
          onClick={() => changeTab('gts')}
        >
          GTS
        </button>

        <button
          type="button"
          className={activeTab === 'gex' ? 'gtm-tab is-active' : 'gtm-tab'}
          onClick={() => changeTab('gex')}
        >
          Call / Put GEX
        </button>

        {displayPoint && activeTab === 'gts' && (
          <>
            <span
              className="gtm-readout gtm-readout-direction"
              style={{ color: trendColor(displayPoint.trendType) }}
            >
              {normalizeTrendType(displayPoint.trendType).toUpperCase()}
            </span>
            <span className="gtm-readout">
              {Number(displayPoint.value).toFixed(2)}
            </span>
          </>
        )}

        {displayPoint && activeTab === 'gex' && (
          <>
            <span className="gtm-readout" style={{ color: COLORS.call }}>
              C {fmtCompact(displayPoint.call)}
            </span>
            <span className="gtm-readout" style={{ color: COLORS.put }}>
              P {fmtCompact(displayPoint.put)}
            </span>
          </>
        )}

        {hoverPoint && (
          <span className="gtm-hover-time">
            {fmtAxisTime(hoverPoint.t)}
          </span>
        )}

        {isZoomed && (
          <button
            type="button"
            className="gtm-reset"
            onClick={resetZoom}
            title="Reset chart zoom"
          >
            Reset
          </button>
        )}

      </WidgetPanelControls>

      <div className="gtm-wrap" ref={wrapRef}>
        {!n ? (
          <div className="gtm-empty">
            Loading...
          </div>
        ) : (
          <svg
            width={VW}
            height={VH}
            className={isPanning ? 'gtm-chart is-panning' : 'gtm-chart'}
            ref={chartWheelRef}
            onPointerMove={handlePointerMove}
            onPointerDown={handlePointerDown}
            onPointerUp={finishPan}
            onPointerCancel={finishPan}
            onPointerLeave={(event) => {
              if (!panRef.current) {
                setHover(null);
                clearFocusedMinute('gexTrendMonitor');
              }

              if (panRef.current && event.buttons === 0) {
                finishPan(event);
              }
            }}
            onDoubleClick={resetZoom}
          >
            {activeTab === 'gts' ? (
              <>
                {/* Low-conviction transition zone. This visually absorbs
                    rapid Call/Put flip chatter without changing source data. */}
                <rect
                  className="gtm-gts-transition-band"
                  x={ML}
                  y={yFn(GTS_TRANSITION_BAND)}
                  width={Math.max(0, VW - MR - ML)}
                  height={Math.max(
                    0,
                    yFn(-GTS_TRANSITION_BAND)
                    - yFn(GTS_TRANSITION_BAND),
                  )}
                />
                <text
                  className="gtm-gts-transition-label"
                  x={VW - MR - 5}
                  y={yFn(0) - 4}
                  textAnchor="end"
                >
                  CONTESTED
                </text>

                {/* Mirrored strength zones. These are visual lanes, not signed
                    source values: CALL lives above the spine, PUT below it. */}
                <rect
                  className="gtm-gts-zone gtm-gts-zone-weak"
                  x={ML}
                  y={yFn(0.20)}
                  width={Math.max(0, VW - MR - ML)}
                  height={Math.max(0, yFn(0) - yFn(0.20))}
                />
                <rect
                  className="gtm-gts-zone gtm-gts-zone-building"
                  x={ML}
                  y={yFn(0.30)}
                  width={Math.max(0, VW - MR - ML)}
                  height={Math.max(0, yFn(0.20) - yFn(0.30))}
                />
                <rect
                  className="gtm-gts-zone gtm-gts-zone-strong"
                  x={ML}
                  y={yFn(0.40)}
                  width={Math.max(0, VW - MR - ML)}
                  height={Math.max(0, yFn(0.30) - yFn(0.40))}
                />
                <rect
                  className="gtm-gts-zone gtm-gts-zone-extreme"
                  x={ML}
                  y={yFn(gtsHi)}
                  width={Math.max(0, VW - MR - ML)}
                  height={Math.max(0, yFn(0.40) - yFn(gtsHi))}
                />

                <rect
                  className="gtm-gts-zone gtm-gts-zone-weak"
                  x={ML}
                  y={yFn(0)}
                  width={Math.max(0, VW - MR - ML)}
                  height={Math.max(0, yFn(-0.20) - yFn(0))}
                />
                <rect
                  className="gtm-gts-zone gtm-gts-zone-building"
                  x={ML}
                  y={yFn(-0.20)}
                  width={Math.max(0, VW - MR - ML)}
                  height={Math.max(0, yFn(-0.30) - yFn(-0.20))}
                />
                <rect
                  className="gtm-gts-zone gtm-gts-zone-strong"
                  x={ML}
                  y={yFn(-0.30)}
                  width={Math.max(0, VW - MR - ML)}
                  height={Math.max(0, yFn(-0.40) - yFn(-0.30))}
                />
                <rect
                  className="gtm-gts-zone gtm-gts-zone-extreme"
                  x={ML}
                  y={yFn(-0.40)}
                  width={Math.max(0, VW - MR - ML)}
                  height={Math.max(0, yFn(-gtsHi) - yFn(-0.40))}
                />

                {/* Mirrored absolute-GTS reference rails. */}
                {GTS_LEVELS.flatMap((level) => [
                  <React.Fragment key={`call:${level.value}`}>
                    <line
                      className={
                        level.value === 0.40
                          ? 'gtm-gts-level is-strong'
                          : 'gtm-gts-level'
                      }
                      x1={ML}
                      x2={VW - MR}
                      y1={yFn(level.value)}
                      y2={yFn(level.value)}
                    />
                    <text
                      className="gtm-axis"
                      x={VW - MR + 4}
                      y={yFn(level.value) + 3}
                    >
                      {level.label}
                    </text>
                  </React.Fragment>,
                  <React.Fragment key={`put:${level.value}`}>
                    <line
                      className={
                        level.value === 0.40
                          ? 'gtm-gts-level is-strong'
                          : 'gtm-gts-level'
                      }
                      x1={ML}
                      x2={VW - MR}
                      y1={yFn(-level.value)}
                      y2={yFn(-level.value)}
                    />
                    <text
                      className="gtm-axis"
                      x={VW - MR + 4}
                      y={yFn(-level.value) + 3}
                    >
                      {level.label}
                    </text>
                  </React.Fragment>,
                ])}

                {/* Center polarity spine. */}
                <line
                  className="gtm-gts-spine"
                  x1={ML}
                  x2={VW - MR}
                  y1={yFn(0)}
                  y2={yFn(0)}
                />
                <text
                  className="gtm-gts-lane-label is-call"
                  x={ML + 4}
                  y={MT + 10}
                >
                  CALL
                </text>
                <text
                  className="gtm-gts-lane-label is-put"
                  x={ML + 4}
                  y={VH - MB - 5}
                >
                  PUT
                </text>

                {/* Pressure stems: magnitude at each minute relative to the
                    categorical polarity spine. */}
                {gtsGeometry.stems.map((stem) => (
                  <line
                    key={stem.key}
                    className="gtm-gts-stem"
                    x1={stem.x}
                    x2={stem.x}
                    y1={stem.yZero}
                    y2={stem.y}
                    stroke={stem.color}
                    style={{ opacity: stem.opacity }}
                  />
                ))}

                {/* Neutral handoff bridges make polarity transitions continuous
                    without implying that raw GTS itself crossed through zero. */}
                {gtsGeometry.bridges.map((bridge) => (
                  <line
                    key={bridge.key}
                    className="gtm-gts-bridge"
                    x1={bridge.x1}
                    y1={bridge.y1}
                    x2={bridge.x2}
                    y2={bridge.y2}
                    style={{ opacity: bridge.opacity }}
                  />
                ))}

                {/* Same-side ribbon trajectory. Line weight grows subtly with
                    uninterrupted persistence; no smoothing is applied. */}
                {gtsGeometry.segments.map((segment) => (
                  <line
                    key={segment.key}
                    className="gtm-gts-segment"
                    x1={segment.x1}
                    y1={segment.y1}
                    x2={segment.x2}
                    y2={segment.y2}
                    stroke={segment.color}
                    style={{
                      strokeWidth: segment.width,
                      opacity: segment.opacity,
                    }}
                  />
                ))}

                {/* A polarity flip is marked on the spine instead of drawing a
                    misleading diagonal path through zero. */}
                {gtsGeometry.flips.map((flip) => (
                  <rect
                    key={flip.key}
                    className="gtm-gts-flip"
                    x={flip.x - 2.8}
                    y={flip.y - 2.8}
                    width="5.6"
                    height="5.6"
                    fill={flip.color}
                    transform={`rotate(45 ${flip.x} ${flip.y})`}
                  />
                ))}
              </>
            ) : (
              <>
                <line
                  className="gtm-zero"
                  x1={ML}
                  x2={VW - MR}
                  y1={yFn(0)}
                  y2={yFn(0)}
                />

                <polyline
                  className="gtm-gex-line"
                  points={visibleSeries.map((point) => `${xTimeFn(point.t)},${yFn(point.call)}`).join(' ')}
                  fill="none"
                  stroke={COLORS.call}
                />
                <polyline
                  className="gtm-gex-line"
                  points={visibleSeries.map((point) => `${xTimeFn(point.t)},${yFn(point.put)}`).join(' ')}
                  fill="none"
                  stroke={COLORS.put}
                />

                {yTicks.map((tick) => (
                  <text
                    key={`${tick.value}:${tick.label}`}
                    className="gtm-axis"
                    x={VW - MR + 4}
                    y={yFn(tick.value) + 3}
                  >
                    {tick.label}
                  </text>
                ))}
              </>
            )}

            {/* Taken from the window, not the series, so a window that
                reserves the empty future is still labelled. */}
            {(() => {
              const all = selectSpacedTimeTicks(
                gexTimeAxisTicks(viewStart, viewEnd),
                (tick) => xTimeFn(tick),
              );

              return all.map((tick, position) => (
                <text
                  key={tick}
                  className="gtm-axis"
                  x={xTimeFn(tick)}
                  y={VH - 5}
                  textAnchor={
                    position === 0
                      ? 'start'
                      : position === all.length - 1
                        ? 'end'
                        : 'middle'
                  }
                >
                  {fmtAxisTime(tick)}
                </text>
              ));
            })()}

            {crosshairPoint && (
              <>
                <line
                  className="gtm-crosshair"
                  x1={hoverX}
                  x2={hoverX}
                  y1={MT}
                  y2={VH - MB}
                />

                {hoverPoint && hoverY != null && (
                  <>
                    <line
                      className="gtm-crosshair"
                      x1={ML}
                      x2={VW - MR}
                      y1={hoverY}
                      y2={hoverY}
                    />

                    <circle
                      className="gtm-crosshair-dot"
                      cx={hoverX}
                      cy={hoverY}
                      r="3"
                      fill={
                        activeTab === 'gts'
                          ? trendColor(hoverPoint.trendType)
                          : COLORS.neutral
                      }
                    />
                  </>
                )}
              </>
            )}
          </svg>
        )}
      </div>
    </div>
  );
}
